import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ListPlus, Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import layerGroupsService, { GroupItemRef, LayerGroups } from '@/services/layerGroups.service';
import { cn } from '@/utils/cn';
import { GROUPS_KEY, useLayerGroups } from './useLayerGroups';

/**
 * "Add to my menu…" on a layer row: my own groups, ticked where the layer is in them already (a click puts it in or
 * takes it out), and a new menu with this layer in it. Nothing for someone who may not make groups.
 */
export const AddToGroupMenu: React.FC<{ item: GroupItemRef; className?: string; subtle?: boolean }> = ({ item, className, subtle = true }) => {
  const { t } = useTranslation();
  // Every layer row has one: only the list is read here; the changes are made by the picker once it is open.
  const { data } = useQuery<LayerGroups>({ queryKey: GROUPS_KEY, queryFn: () => layerGroupsService.list(), retry: false });
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  if (!data?.canCreate) return null;
  const mine = data.groups.filter((g) => g.mine);
  const inAny = mine.some((g) => g.items.some((i) => i.kind === item.kind && i.ref === item.ref));

  return (
    <div className={cn('relative', className)} ref={box} onClick={(e) => e.stopPropagation()}>
      <button type="button" data-testid="add-to-group"
              className={cn('p-0.5 rounded transition-colors', inAny ? 'text-gn-blue' : 'text-gn-muted hover:text-gn-blue',
                !inAny && subtle && 'opacity-0 group-hover:opacity-100 focus:opacity-100')}
              title={t('layerGroups.add_to')} aria-label={t('layerGroups.add_to')} aria-expanded={open} onClick={() => setOpen(!open)}>
        <ListPlus size={11} />
      </button>
      {open && <Picker item={item} data={data} onDone={() => setOpen(false)} />}
    </div>
  );
};

const Picker: React.FC<{ item: GroupItemRef; data: LayerGroups; onDone: () => void }> = ({ item, data, onDone }) => {
  const { t } = useTranslation();
  const { create, toggleItem } = useLayerGroups();
  const [name, setName] = useState('');
  const mine = data.groups.filter((g) => g.mine);
  return (
    <div role="menu" className="absolute right-0 top-5 z-50 w-52 rounded border border-gn-light bg-gn-surface shadow-lg p-1 text-xs">
      <p className="px-2 py-1 text-[10px] uppercase tracking-wide text-gn-muted">{t('layerGroups.add_to')}</p>
      {mine.length === 0 && <p className="px-2 py-1 text-gn-muted italic">{t('layerGroups.none_yet')}</p>}
      {mine.map((g) => {
        const has = g.items.some((i) => i.kind === item.kind && i.ref === item.ref);
        return (
          <button key={g.id} type="button" role="menuitemcheckbox" aria-checked={has}
                  className="flex w-full items-center gap-2 px-2 py-1 rounded hover:bg-gn-tertiary text-left"
                  onClick={() => toggleItem(g, item)}>
            <span className="w-3">{has && <Check size={12} className="text-gn-blue" />}</span>
            {g.colour && <span className="inline-block w-2 h-2 rounded-full" style={{ background: g.colour }} />}
            <span className="flex-1 truncate">{g.name}</span>
          </button>
        );
      })}
      <form className="flex gap-1 border-t border-gn-light mt-1 pt-1" onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        create.mutate({ name: name.trim(), items: [{ kind: item.kind, ref: item.ref }] }, { onSuccess: () => { setName(''); onDone(); } });
      }}>
        <input className="flex-1 min-w-0 px-2 py-1 border border-gn-light rounded bg-gn-surface text-gn-primary text-xs"
               placeholder={t('layerGroups.new_with_layer')} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
        <button type="submit" className="p-1 rounded hover:bg-gn-tertiary text-gn-secondary disabled:opacity-40"
                disabled={!name.trim() || create.isPending} title={t('layerGroups.create')}>
          <Plus size={13} />
        </button>
      </form>
    </div>
  );
};

export default AddToGroupMenu;
